import { useState, useEffect, useRef } from 'react';
import { Camera, ImagePlus, X } from 'lucide-react';
import { CameraCapture, CaptureMode } from './CameraCapture';
import { uploadPhotoFile, downscaleImageForUpload, PhotoBucket } from '../lib/photoUpload';
import { enqueuePhoto, isLocalMarker, resolveMarkerToObjectUrl } from '../lib/photoQueue';
import { logEvent } from '../lib/telemetry';
import { resolvePhotoLocationDetailed, prunePhotoGeo, type PhotoGeo, type PhotoGeoMap } from '../lib/photoLocation';

interface PhotoCaptureProps {
  photoUrls: string[];
  bucket: PhotoBucket;
  pathPrefix: string;
  /**
   * Apply a photo change. `geo` travels WITH the urls, in one call, on purpose.
   *
   * This used to be two callbacks — set the urls, then set the locations — and
   * the consumers rebuild their state from a closure snapshot, so the second
   * call overwrote whatever the first had just written. The urls always won and
   * every coordinate was silently discarded on its way to the database (bug
   * fa03402b: 0 of 329 photos kept a location). One update cannot race itself.
   */
  onPhotosChange: (urls: string[], geo?: PhotoGeoMap) => void;
  /**
   * Local draft this photo belongs to. When set, a photo taken offline (or one
   * whose upload fails) is queued to IndexedDB and represented by a
   * local-photo:// marker instead of being lost; the sync engine uploads it on
   * reconnect. Without it (e.g. device-management forms) we keep the old
   * upload-or-discard behavior.
   */
  localDraftId?: string;
  /**
   * Where each photo was taken, keyed by the same string that appears in
   * `photoUrls` (a real URL, or a local-photo:// marker while it is still
   * queued). Optional: a form that does not pass these simply captures no
   * location, and photos behave exactly as before.
   */
  /**
   * Present (even as {}) means this form stores photo locations; absent means it
   * does not and no location is resolved at all. That is the capability flag.
   */
  photoGeo?: PhotoGeoMap;
}

export function PhotoCapture({
  photoUrls,
  bucket,
  pathPrefix,
  onPhotosChange,
  localDraftId,
  photoGeo,
}: PhotoCaptureProps) {
  const [captureMode, setCaptureMode] = useState<CaptureMode | null>(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // marker -> object URL, so a pending offline photo still shows a preview.
  const [resolved, setResolved] = useState<Record<string, string>>({});
  const objectUrlsRef = useRef<string[]>([]);

  // Resolve any local-photo:// markers in the list to object URLs for display.
  useEffect(() => {
    const pending = photoUrls.filter((u) => isLocalMarker(u) && !resolved[u]);
    if (pending.length === 0) return;
    let cancelled = false;
    (async () => {
      const next: Record<string, string> = {};
      for (const marker of pending) {
        const obj = await resolveMarkerToObjectUrl(marker);
        if (obj) {
          next[marker] = obj;
          objectUrlsRef.current.push(obj);
        }
      }
      if (!cancelled && Object.keys(next).length) setResolved((prev) => ({ ...prev, ...next }));
    })();
    return () => {
      cancelled = true;
    };
  }, [photoUrls, resolved]);

  // Revoke any object URLs we created when the component unmounts.
  useEffect(() => () => objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const queuePhoto = async (file: File): Promise<string> => {
    const prepared = await downscaleImageForUpload(file).catch(() => file);
    return enqueuePhoto({
      blob: prepared,
      bucket,
      path: `${pathPrefix}_${Date.now()}.jpg`,
      localDraftId: localDraftId!,
    });
  };

  /** Does this form store photo locations at all? */
  const capturesGeo = photoGeo !== undefined;

  /**
   * The geo map to send ALONGSIDE a url change, with this photo's location
   * added under whatever key currently represents it (a real URL, or a
   * local-photo:// marker while it is still queued).
   *
   * Returns undefined when there is nothing to change, which tells the consumer
   * to leave the existing locations alone rather than replace them with {}.
   */
  const geoWith = (key: string, geo: PhotoGeo | null): PhotoGeoMap | undefined => {
    if (!capturesGeo || !geo) return undefined;
    return { ...(photoGeo ?? {}), [key]: geo };
  };

  const handleCapture = async (file: File) => {
    setCaptureMode(null);
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

    // Resolve location from the ORIGINAL file, before anything downscales it --
    // the canvas re-encode strips EXIF, and this is the only moment the tech is
    // demonstrably standing where the photo was taken. Never prompts; returns
    // null when no location is available, which just means no pin.
    let geo: PhotoGeo | null = null;
    if (capturesGeo) {
      const resolved = await resolvePhotoLocationDetailed(file);
      geo = resolved.geo;
      // Record WHY, every time — including (especially) when nothing was found.
      // Without this a photo with no pin is indistinguishable from a photo whose
      // location was never attempted, which is exactly why this feature could
      // ship recording nothing and go unnoticed. Read it in Settings -> Event Log.
      void logEvent('photo_location_resolved', `Photo location ${resolved.diag.src} for ${bucket}`, {
        metadata: { bucket, path_prefix: pathPrefix, ...resolved.diag },
      });
    }

    // Offline with a draft to queue under: store locally, show via marker.
    if (offline && localDraftId) {
      const marker = await queuePhoto(file);
      onPhotosChange([...photoUrls, marker], geoWith(marker, geo));
      void logEvent('inspection_photo_queued_offline', `Photo queued offline for ${bucket}`, {
        metadata: { bucket, path_prefix: pathPrefix, size_bytes: file.size },
      });
      return;
    }

    // Online: optimistic preview then upload.
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);
    const optimisticUrls = [...photoUrls, localPreview];
    onPhotosChange(optimisticUrls);

    setUploading(true);
    const startedAt = Date.now();
    try {
      const path = `${pathPrefix}_${Date.now()}.jpg`;
      const publicUrl = await uploadPhotoFile(file, bucket, path);
      onPhotosChange(optimisticUrls.map((u) => (u === localPreview ? publicUrl : u)), geoWith(publicUrl, geo));
      setPreviewUrl(null);
      URL.revokeObjectURL(localPreview);
      void logEvent('inspection_photo_attached', `Photo uploaded to ${bucket}`, {
        metadata: { bucket, path_prefix: pathPrefix, size_bytes: file.size, duration_ms: Date.now() - startedAt },
      });
    } catch (err) {
      setPreviewUrl(null);
      URL.revokeObjectURL(localPreview);
      if (localDraftId) {
        // Upload failed (lost signal mid-transfer) — queue it instead of losing it.
        const marker = await queuePhoto(file);
        onPhotosChange(optimisticUrls.map((u) => (u === localPreview ? marker : u)), geoWith(marker, geo));
        void logEvent('inspection_photo_queued_offline', `Photo upload failed, queued for ${bucket}`, {
          metadata: { bucket, path_prefix: pathPrefix, size_bytes: file.size, error_message: err instanceof Error ? err.message : String(err) },
        });
      } else {
        console.error('Photo upload failed:', err);
        onPhotosChange(photoUrls);
        void logEvent('inspection_photo_upload_failed', `Photo upload failed for ${bucket}`, {
          severity: 'error',
          metadata: { bucket, path_prefix: pathPrefix, size_bytes: file.size, error_message: err instanceof Error ? err.message : String(err) },
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (url: string) => {
    const remaining = photoUrls.filter((u) => u !== url);
    // Prune in the SAME update as the url change. Sent separately it was
    // clobbered exactly like the capture path was.
    onPhotosChange(remaining, photoGeo ? prunePhotoGeo(photoGeo, remaining) : undefined);
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {photoUrls.map((url, index) => {
          const pending = isLocalMarker(url);
          const src = pending ? (resolved[url] ?? '') : url;
          return (
            <div key={index} className="relative">
              <img
                src={src}
                alt={`Photo ${index + 1}`}
                className="w-20 h-20 object-cover rounded-lg border border-line bg-subtle"
              />
              <button
                type="button"
                onClick={() => handleRemove(url)}
                disabled={url === previewUrl && uploading}
                className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full transition-opacity disabled:opacity-30"
              >
                <X size={12} />
              </button>
              {pending && (
                <div className="absolute bottom-0 inset-x-0 bg-amber-500/90 text-white text-[8px] font-semibold text-center rounded-b-lg py-0.5">
                  pending
                </div>
              )}
              {url === previewUrl && uploading && (
                <div className="absolute inset-0 bg-white/60 rounded-lg flex items-center justify-center">{/* theme-allow: light spinner scrim over the photo thumbnail */}
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => setCaptureMode('camera')}
          disabled={uploading}
          className="w-20 h-20 border-2 border-dashed border-line rounded-lg flex flex-col items-center justify-center hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors disabled:opacity-50 active:scale-95"
        >
          <Camera size={20} className="text-theme-secondary mb-0.5" />
          <span className="text-[10px] text-theme-secondary">Camera</span>
        </button>
        <button
          type="button"
          onClick={() => setCaptureMode('gallery')}
          disabled={uploading}
          className="w-20 h-20 border-2 border-dashed border-line rounded-lg flex flex-col items-center justify-center hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors disabled:opacity-50 active:scale-95"
        >
          <ImagePlus size={20} className="text-theme-secondary mb-0.5" />
          <span className="text-[10px] text-theme-secondary">Gallery</span>
        </button>
      </div>

      {captureMode && (
        <CameraCapture
          onCapture={handleCapture}
          onClose={() => setCaptureMode(null)}
          mode={captureMode}
        />
      )}
    </>
  );
}
